import { useCallback } from "react";

import {
  AccountCashPayload,
  DirectedAccountCashPayload,
  useRecordAccountFeeMutation,
  useRecordAdjustmentMutation,
  useRecordInterestMutation,
} from "../../../../services";
import { formatDate } from "../../../../utils";
import {
  AccountCashFormValues,
  AccountCashSubmitHelpers,
  mapAccountCashError,
  showsInterestTax,
} from "../../panels";
import { noteFields } from "./noteFields";
import { submitLedgerCommand } from "./submitLedgerCommand";
import {
  UseAccountCashCommandsProps,
  UseAccountCashCommandsReturn,
} from "./types";

// The ledger commands that name no position: an account-level fee, interest on
// the broker's own cash, and a residual the broker settled against it.
export const useAccountCashCommands = ({
  investmentId,
  accountId,
  onRecorded,
  settlementAccountName,
}: UseAccountCashCommandsProps): UseAccountCashCommandsReturn => {
  const isLinkedSettlement = settlementAccountName !== null;
  const [recordAccountFeeTrigger] = useRecordAccountFeeMutation();
  const [recordInterestTrigger] = useRecordInterestMutation();
  const [recordAdjustmentTrigger] = useRecordAdjustmentMutation();

  // Branched like the trade handler: payloads are genuinely different shapes, and the server 400s an account fee carrying `direction`.
  // No manual refetch — holdingTradeRipple invalidates the ledger, trade history, investment and net-worth cards, exactly as a trade does.
  const recordAccountCash = useCallback(
    async (
      values: AccountCashFormValues,
      helpers: AccountCashSubmitHelpers,
    ) => {
      // The two directed types differ only in trigger and wording, so they share one branch;
      // the fee keeps its own because its payload genuinely omits `direction`.
      const DIRECTED: Partial<
        Record<
          AccountCashFormValues["action"],
          [typeof recordInterestTrigger, string]
        >
      > = {
        interest: [recordInterestTrigger, "Interest recorded"],
        adjustment: [recordAdjustmentTrigger, "Adjustment recorded"],
      };

      const shared = {
        accountId,
        amountEur: Number(values.amountEur),
        effectiveDate: formatDate(values.date),
        ...noteFields(values.note),
      };

      const record = () => {
        const directed = DIRECTED[values.action];
        if (directed) {
          // Sent only while the field is shown; @Min(0.01) rejects zero, so a blank field omits the key.
          const hasTax = showsInterestTax(values) && values.taxWithheld !== "";
          const payload: DirectedAccountCashPayload = {
            ...shared,
            direction: values.direction,
            ...(hasTax ? { taxWithheld: Number(values.taxWithheld) } : {}),
          };
          const [trigger, successMessage] = directed;
          return {
            promise: trigger({
              investmentId,
              payload,
              movesTransactions: isLinkedSettlement,
            }).unwrap(),
            successMessage,
          };
        }
        const payload: AccountCashPayload = shared;
        return {
          promise: recordAccountFeeTrigger({
            investmentId,
            payload,
            movesTransactions: isLinkedSettlement,
          }).unwrap(),
          successMessage: "Account fee recorded",
        };
      };

      await submitLedgerCommand<AccountCashFormValues>({
        ...record(),
        onRecorded,
        helpers,
        mapError: mapAccountCashError,
        settlementAccountName,
      });
    },
    [
      accountId,
      investmentId,
      recordInterestTrigger,
      recordAdjustmentTrigger,
      recordAccountFeeTrigger,
      onRecorded,
      isLinkedSettlement,
      settlementAccountName,
    ],
  );

  return { recordAccountCash };
};
